import classNames from 'classnames';
import { cloneDeep } from 'lodash';
import React, { useRef, useState } from 'react';
import { Message } from '@meitu/saas-ui';
import IconFont from '@meitu/xdesign-icon-font';
import {
    getOperateDirection,
    getOperateType,
    useActiveStageIndex,
    useFramework,
    useIsPickStage,
    useMoveEventListener,
    useStageList,
} from '@/framework';
import { useLocaleStore } from '@/i18n/index';
import { useResizeCursor } from '@/utils/cursor-kit';
import { ITouchKitEvent } from '@/utils/event-kit';
import { traverseTrees } from '@/utils/tree-kit';
import { parseToUnit } from '@/utils/unit-kit';
import {
    BaseLayerViewType,
    ILayerProps,
    IPoint,
    ISize,
    MAX_STAGE_HEIGHT,
    MAX_STAGE_WIDTH,
    MIN_STAGE_HEIGHT,
    MIN_STAGE_WIDTH,
    REFERENCE_LINE_EVENT_NAMES,
    ResizeOperateType,
    type ReferenceLine,
} from '@/version-types';
import { type ReferenceLines } from '../reference-lines';
import { QuickAdjustOperate } from './operate/quick-adjust-operate';
import { QuickStageOperate } from './operate/quick-stage-operate';

/**
 * 单画布舞台尺寸调整
 */
export const AdjustOperate: React.FC = React.memo(() => {
    const { BC, LC, RC, TC } = useResizeCursor(0);
    const { $t } = useLocaleStore();
    const activeStageIndex = useActiveStageIndex();
    const count = useStageList().length;
    const isPickStage = useIsPickStage();
    const framework = useFramework();
    const [operateType, setOperateType] = useState<ResizeOperateType | null>(null);
    const originSize = useRef<ISize>({ width: 0, height: 0 });
    const adjustOriginIndex = useRef(0);
    const originPosition = useRef<Map<string, IPoint>>(new Map());
    const operateBarRef = useRef<any>(null);
    const [localReferenceLines, setLocalReferenceLines] = useState<ReferenceLine[]>([]);

    useMoveEventListener((e: ITouchKitEvent) => {
        const target = e.target as HTMLElement;
        const operateType = getOperateType(target);

        if (operateType !== BaseLayerViewType.STAGE_RESIZE) return;
        const direction = getOperateDirection(target);
        if (!direction) return;
        const adjustType = direction;
        const cls = adjustType === ResizeOperateType.TC || adjustType === ResizeOperateType.BC ? TC : LC;
        if (e.isStart) {
            framework.getInstance(QuickAdjustOperate).emitAdjust({
                isStart: true,
                isEnd: false,
                type: direction,
            });

            const originIndex = framework.stage.getActiveOriginIndex();
            originSize.current = framework.getSize(originIndex);
            adjustOriginIndex.current = originIndex;
            const layers = framework.getLayers(originIndex);
            originPosition.current.clear();
            traverseTrees(layers, (layer: ILayerProps) => {
                originPosition.current.set(layer.id, {
                    x: layer.left,
                    y: layer.top,
                });
            });
            operateBarRef.current = document.querySelector(
                `#${framework.identity} .c-adjust-resize[data-direction=${adjustType}] > div`,
            );

            document.body.classList.add(cls);

            // 发送舞台尺寸变化前事件
            framework.events.emit(REFERENCE_LINE_EVENT_NAMES.SYNC_STAGE_RESIZE_START);
            // 保存修改前的参考线数据， 用于历史记录回退
            const referenceLines =
                framework.getInstance<ReferenceLines>('ReferenceLines')?.getReferenceLineData().lines || [];
            setLocalReferenceLines(referenceLines);
        }
        const scale = framework.stage.getScale();
        const offsetX = ~~(e.displacementX / scale);
        const offsetY = ~~(e.displacementY / scale);
        const newSize = cloneDeep(originSize.current);
        let tip = '';
        let tipOffsetX = 0;
        let tipOffsetY = 0;
        let maxHeight;
        let maxWidth;
        const unit = framework.stage.getStageUnit();
        switch (adjustType) {
            case ResizeOperateType.BC:
                {
                    maxHeight = Math.floor(originSize.current.height + offsetY);
                    if (maxHeight > MAX_STAGE_HEIGHT) {
                        maxHeight = MAX_STAGE_HEIGHT;
                        Message.error($t((v) => v.stageManager.canvasLimitScope));
                    }
                    newSize.height = Math.max(MIN_STAGE_HEIGHT, maxHeight);
                    tip = `${$t((v) => v.stageManager.height)} ${parseToUnit(newSize.height, unit)}${unit}`;
                    tipOffsetX = 15;
                }
                break;
            case ResizeOperateType.TC:
                {
                    maxHeight = Math.floor(originSize.current.height - offsetY);
                    if (maxHeight > MAX_STAGE_HEIGHT) {
                        maxHeight = MAX_STAGE_HEIGHT;
                        Message.error($t((v) => v.stageManager.canvasLimitScope));
                    }
                    newSize.height = Math.max(MIN_STAGE_HEIGHT, maxHeight);
                    tip = `${$t((v) => v.stageManager.height)} ${parseToUnit(newSize.height, unit)}${unit}`;
                    tipOffsetX = 15;
                }
                break;
            case ResizeOperateType.RC:
                {
                    maxWidth = Math.floor(originSize.current.width + offsetX);
                    if (maxWidth > MAX_STAGE_WIDTH) {
                        maxWidth = MAX_STAGE_WIDTH;
                        Message.error($t((v) => v.stageManager.canvasLimitScope));
                    }
                    newSize.width = Math.max(MIN_STAGE_WIDTH, maxWidth);
                    tip = `${$t((v) => v.stageManager.width)} ${parseToUnit(newSize.width, unit)}${unit}`;
                    tipOffsetX = -50;
                    tipOffsetY = 45;
                }
                break;
            case ResizeOperateType.LC:
                {
                    maxWidth = Math.floor(originSize.current.width - offsetX);
                    if (maxWidth > MAX_STAGE_WIDTH) {
                        maxWidth = MAX_STAGE_WIDTH;
                        Message.error($t((v) => v.stageManager.canvasLimitScope));
                    }
                    newSize.width = Math.max(MIN_STAGE_WIDTH, maxWidth);
                    tip = `${$t((v) => v.stageManager.width)} ${parseToUnit(newSize.width, unit)}${unit}`;
                    tipOffsetX = -50;
                    tipOffsetY = 45;
                }
                break;
        }

        framework
            .getInstance(QuickAdjustOperate)
            .doAdjust(adjustOriginIndex.current, originSize.current, newSize, adjustType, originPosition.current)
            .send(adjustOriginIndex.current, originSize.current, newSize, adjustType, e.isStart, e.isEnd);

        if (tip && operateBarRef.current) {
            const rect = operateBarRef.current?.getBoundingClientRect();
            framework.operate.cursorTip.set({
                type: 'custom',
                x: rect.x + tipOffsetX,
                y: rect.y + tipOffsetY,
                txt: tip,
                hideCorner: true,
            });
        }

        if (e.isEnd) {
            framework.operate.cursorTip.clear();
            operateBarRef.current = null;
            setOperateType(null);
            framework.operate.setIsBaseOperate(false);
            originPosition.current.clear();
            framework
                .getInstance(QuickAdjustOperate)
                .emitAdjust({
                    isStart: false,
                    isEnd: true,
                    type: direction,
                })
                .write(
                    adjustOriginIndex.current,
                    originSize.current,
                    newSize,
                    localReferenceLines,
                    framework.getInstance<ReferenceLines>('ReferenceLines')?.getReferenceLineData().lines || [],
                    unit,
                    adjustType,
                );
            document.body.classList.remove(cls);

            // 发送舞台尺寸变化后事件
            framework.events.emit(REFERENCE_LINE_EVENT_NAMES.SYNC_STAGE_RESIZE_END);
        }

        // 发送舞台尺寸调整事件
        if (!e.isEnd && !e.isStart) {
            const isVertical = adjustType === ResizeOperateType.TC || adjustType === ResizeOperateType.BC;
            // 通知参考线同步调整位置， 以保存跟图层位置的相对关系
            framework.events.emit(REFERENCE_LINE_EVENT_NAMES.SYNC_STAGE_RESIZING, {
                newSize: isVertical ? newSize.height : newSize.width,
                oldSize: isVertical ? originSize.current.height : originSize.current.width,
                operateType: adjustType,
                stageIndex: framework.convertOriginToStage(adjustOriginIndex.current),
            });
        }
    });

    return (
        <>
            {/* 单画布顶部页面 */}
            <div
                className="c-quick-stage-info"
                onClick={() => {
                    framework.getInstance(QuickStageOperate).emitQuickClick('NAVIGATOR_MENU', true);
                }}
                onMouseDown={(e) => e.stopPropagation()}
            >
                <IconFont name="TextListBold" fontSize={12} />
                <div className="text">
                    {$t((v) => v.stageManager.page)} {activeStageIndex + 1}/{count}
                </div>
            </div>
            {isPickStage ? (
                <>
                    <div
                        data-operate-type={BaseLayerViewType.STAGE_RESIZE}
                        data-direction={ResizeOperateType.TC}
                        className={classNames('c-adjust-resize', 'tc', TC)}
                        onMouseDown={() => {
                            framework.operate.setIsBaseOperate(true);
                            setOperateType(ResizeOperateType.TC);
                        }}
                        onMouseUp={() => {
                            framework.operate.setIsBaseOperate(false);
                            setOperateType(null);
                        }}
                    >
                        <div
                            className={classNames('c-adjust-resize-h-bar', {
                                active: operateType === ResizeOperateType.TC,
                            })}
                        ></div>
                    </div>
                    <div
                        data-operate-type={BaseLayerViewType.STAGE_RESIZE}
                        data-direction={ResizeOperateType.BC}
                        className={classNames('c-adjust-resize', 'bc', BC)}
                        onMouseDown={() => {
                            framework.operate.setIsBaseOperate(true);
                            setOperateType(ResizeOperateType.BC);
                        }}
                        onMouseUp={() => {
                            framework.operate.setIsBaseOperate(false);
                            setOperateType(null);
                        }}
                    >
                        <div
                            className={classNames('c-adjust-resize-h-bar', {
                                active: operateType === ResizeOperateType.BC,
                            })}
                        ></div>
                    </div>
                    <div
                        data-operate-type={BaseLayerViewType.STAGE_RESIZE}
                        data-direction={ResizeOperateType.LC}
                        className={classNames('c-adjust-resize', 'lc', LC)}
                        onMouseDown={() => {
                            framework.operate.setIsBaseOperate(true);
                            setOperateType(ResizeOperateType.LC);
                        }}
                        onMouseUp={() => {
                            framework.operate.setIsBaseOperate(false);
                            setOperateType(null);
                        }}
                    >
                        <div
                            className={classNames('c-adjust-resize-v-bar', {
                                active: operateType === ResizeOperateType.LC,
                            })}
                        ></div>
                    </div>
                    <div
                        data-operate-type={BaseLayerViewType.STAGE_RESIZE}
                        data-direction={ResizeOperateType.RC}
                        className={classNames('c-adjust-resize', 'rc', RC)}
                        onMouseDown={() => {
                            framework.operate.setIsBaseOperate(true);
                            setOperateType(ResizeOperateType.RC);
                        }}
                        onMouseUp={() => {
                            framework.operate.setIsBaseOperate(false);
                            setOperateType(null);
                        }}
                    >
                        <div
                            className={classNames('c-adjust-resize-v-bar', {
                                active: operateType === ResizeOperateType.RC,
                            })}
                        ></div>
                    </div>
                </>
            ) : null}
        </>
    );
});
